import {
    BaselineType,
    IBurstDataChild,
    ISpectrumChartResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { AxisChartSeriesData, Point } from 'shared/chart/core/types';
import { queryClient } from 'utils/query';

const queryKey = 'time-domain-burst-data';

export function invalidateTimeDomainData() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

type UseTimeDomainDataDataOptions = {
    includeBaseline?: boolean;
    returnVelocity?: boolean;
};

export type SeriesData = Pick<
    AxisChartSeriesData,
    'dataPoints' | 'seriesMaxY' | 'seriesMinY' | 'seriesUnit' | 'seriesId'
>;

export type TimeDomainData = Readonly<{
    timeData: SeriesData;
    baselineSeriesData: SeriesData | undefined;
    isVibrationType: boolean;
}>;

function mapBurstDataToPoints({ Data, Rate }: IBurstDataChild): Point[] {
    return Data.map((amplitude, index) => ({
        y: amplitude,
        x: (index * 1000) / Rate,
    }));
}

function mapSeries(data: IBurstDataChild, seriesId: string): SeriesData {
    const dataPoints = mapBurstDataToPoints(data);

    return {
        seriesId,
        seriesUnit: data.Units,
        seriesMinY: Math.min(...data.Data),
        seriesMaxY: Math.max(...data.Data),
        dataPoints,
    };
}

function mapTimeData(data: ISpectrumChartResponse, indicatorId: string): TimeDomainData {
    const timeData = mapSeries(data.BurstData[0], indicatorId);

    // Ranged baselines should not appear on the time waveform plot because they do
    // not provide helpful information to the user. This could change in the future,
    // but for now we disable it.
    const baselineSeriesData =
        data.Baseline?.BaselineType === BaselineType.Single && data.BurstData[1]
            ? mapSeries(data.BurstData[1], `${indicatorId}-baseline`)
            : undefined;

    const isVibrationType = data.IsVibrationTypeIndicator;

    return { timeData, baselineSeriesData, isVibrationType };
}

export function useTimeDomainData(
    indicatorId: string,
    time: Date,
    options?: UseTimeDomainDataDataOptions
) {
    return useQuery({
        queryKey: [queryKey, indicatorId, time, options],

        queryFn: () =>
            sdClient.getBurstData({
                indicatorId,
                includeBaseline: options?.includeBaseline ?? false,
                returnTimeDomain: true,
                returnVelocity: options?.returnVelocity ?? false,
                dateTimestamp: time,
            }),

        select: (data) => mapTimeData(data, indicatorId),
    });
}
